import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Button, Form } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { hasEnvelope, pointerTo } from "./envelopePointer";
import { parsePayload, buildUpdatedPayload, pointerToken } from "./payload";
import { useSelector } from "react-redux";
import DataTable from "@components/DataTable";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import SlideOutMenuV2 from "@components/SlideOutMenuV2";
import SearchField from "@components/Forms/SearchField/SearchField";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert, snakeToText } from "@utils/helperFunctions";
import { RootState } from "@reducers/rootReducer";
import { useV2UpdateBlueprintMutation } from "@generated";
import {
  useApprovedCatalog,
  useListCatalogServices,
} from "@common/hooks/serviceCatalog";
import {
  conversionAttributes,
  useGetAccount,
  useListPhases,
} from "../../../common/api";
import useFlags from "@common/hooks/useFlags";
import type { BlueprintDetail } from "../../../common/api";
import BlueprintServiceEditor from "./BlueprintServiceEditor";
import { BpPhase, BpService } from "./types";
import { unapprovedServiceIds } from "./unapprovedIds";
import "./style.css";

export interface ServicesTabHandle {
  getPayloadUpdates: (basePayload: any[]) => any[];
}

interface Props {
  blueprint: BlueprintDetail;
  refetch: () => void;
  serviceType: "professional_services" | "managed_services";
  readOnly?: boolean;
}

interface CatalogRow {
  id: string;
  name: string;
  serviceCategory: string;
  hours: string;
}

const catalogColumns: ColumnDef<CatalogRow>[] = [
  { header: "Name", cell: (props) => props.row.original.name },
  { header: "Service Category", accessorKey: "serviceCategory" },
  { header: "Hours", accessorKey: "hours" },
];

const ServicesTab = forwardRef<ServicesTabHandle, Props>(
  ({ blueprint, refetch, serviceType, readOnly }, ref) => {
    const { accountId } = useGetAccount();
    const { accountSlug } = useSelector((state: RootState) => state.slug);
    const { phases: allPhases, phasesLoading } = useListPhases();
    const { configHideEmptyPhases } = useFlags();
    const [updateBlueprint] = useV2UpdateBlueprintMutation();

    const blueprintId = Number(blueprint.id);

    // ── DnD state ──
    const [localPhases, setLocalPhases] = useState<BpPhase[]>([]);
    const [localServices, setLocalServices] = useState<BpService[]>([]);
    const [initialized, setInitialized] = useState(false);

    // ── Catalog slideout state ──
    const [slideoutOpen, setSlideoutOpen] = useState(false);
    const [selectedRawServices, setSelectedRawServices] = useState<any[]>([]);
    const selectedAddIds = selectedRawServices.map((s) => String(s.id));

    const handleCatalogSelection = (newIds: string[]) => {
      setSelectedRawServices((prev) => {
        const kept = prev.filter((s) => newIds.includes(String(s.id)));
        const addedIds = newIds.filter(
          (id) => !prev.some((s) => String(s.id) === id)
        );
        const added = (catalogData?.data ?? []).filter((s) =>
          addedIds.includes(String(s.id))
        );
        return [...kept, ...added];
      });
    };
    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [catalogPage, setCatalogPage] = useState(1);
    const [catalogPageSize, setCatalogPageSize] = useState(10);

    // ── Delete state ──
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // ── Alerts ──
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showFailAlert, setShowFailAlert] = useState(false);

    // ── Catalog query ──
    const { data: catalogData, isLoading: catalogLoading } =
      useListCatalogServices(
        {
          include: ["service-category", "phase"],
          filter: {
            service_type: serviceType,
            state: ["active"],
            name: searchQuery || undefined,
          },
          page: { number: catalogPage, size: catalogPageSize },
        },
        { skip: !accountSlug || !slideoutOpen }
      );

    // ── Approval lookup (release.approved-services) ──
    const approvedCatalog = useApprovedCatalog();
    const catalogIds = useMemo(
      () =>
        Array.from(
          new Set(
            localServices
              .map((s) => s.catalogId)
              .filter((id): id is string => !!id)
          )
        ).sort(),
      [localServices]
    );
    const { data: approvedLookup, isFetching: approvedFetching } =
      useListCatalogServices(
        { filter: { ids: catalogIds }, page: { number: 1, size: 500 } },
        { skip: !approvedCatalog || catalogIds.length === 0 }
      );
    const unapprovedIds = useMemo(
      () =>
        approvedCatalog && approvedLookup && !approvedFetching
          ? unapprovedServiceIds(
              localServices,
              (approvedLookup.data ?? []).map((s) => String(s.id))
            )
          : new Set<string>(),
      [approvedCatalog, approvedLookup, approvedFetching, localServices]
    );

    // ── Derived values ──
    const currentPayload = blueprint.rawPayload;
    const projectResources = currentPayload
      .filter((p) => p.type === "project-resources")
      .map((p) => ({
        id: String(p.id ?? p.lid),
        name: p.attributes?.name ?? "",
      }));
    const isEmpty = localServices.length === 0;
    const selectedCount = localServices.filter((s) => s.checked).length;
    const emptyMessage = `No ${snakeToText(serviceType).toLowerCase()} added. ${
      readOnly ? "" : `Click 'Add Services' to get started.`
    }`;

    const catalogItems: CatalogRow[] = (catalogData?.data ?? []).map((svc) => {
      const category = catalogData?.included?.find(
        (i) =>
          i.type === "service-categories" &&
          i.id === (svc as any).relationships?.["service-category"]?.data?.id
      );
      const totalHours = (svc.attributes as any)?.["total-hours"];

      return {
        id: String(svc.id),
        name: svc.attributes?.["nested-name"] || svc.attributes?.name || "",
        serviceCategory: (category as any)?.attributes?.name ?? "",
        hours: totalHours ? parseFloat(String(totalHours)).toFixed(1) : "",
      };
    });

    // ── Parse payload on first load ──
    useEffect(() => {
      if (!phasesLoading) {
        if (initialized) return;
        const { phases, services } = parsePayload(
          currentPayload,
          serviceType,
          allPhases
        );
        setLocalPhases(phases);
        setLocalServices(services);
        setInitialized(true);
      }
    }, [currentPayload, initialized, phasesLoading]);

    // ── Payload patch helper ──
    const patchPayload = async (newPayload: any[]) => {
      await updateBlueprint({
        id: String(blueprintId),
        body: {
          data: {
            id: String(blueprintId),
            type: "blueprints",
            attributes: {
              name: blueprint.name,
              payload: newPayload as any,
              ...conversionAttributes(blueprint.dataVersion),
            },
            relationships: {
              account: { data: { type: "accounts", id: String(accountId) } },
            },
          },
        },
      }).unwrap();
      await refetch();
    };

    // ── Add selected catalog services ──
    const handleAdd = async () => {
      const toAdd: any[] = [];
      const batchPhaseLids: Record<string, string> = {};

      const startPosition = localServices.length + 1;

      selectedRawServices.forEach((rawSvc, idx) => {
        const catalogPhaseId = (rawSvc as any)?.relationships?.phase?.data?.id;

        let phaseRelId: string | number | null = null;

        if (serviceType === "professional_services") {
          const payloadPhase = currentPayload.find(
            (p) =>
              p.type === "project-phases" &&
              String(p.relationships?.phase?.data?.id) ===
                String(catalogPhaseId)
          );

          if (payloadPhase) {
            phaseRelId = payloadPhase.id ?? payloadPhase.lid ?? "";
          } else {
            const catalogPhase = allPhases.find(
              (p) => String(p.id) === String(catalogPhaseId)
            );
            const defaultPhaseId = localPhases?.[0]?.id;

            if (catalogPhase) {
              if (!batchPhaseLids[catalogPhase.id]) {
                const lid = crypto.randomUUID();
                batchPhaseLids[catalogPhase.id] = lid;
                toAdd.push({
                  type: "project-phases",
                  lid,
                  attributes: {
                    name: catalogPhase.name,
                    position: catalogPhase.position,
                  },
                  relationships: {
                    phase: {
                      data: { type: "phases", id: Number(catalogPhase.id) },
                    },
                  },
                });
              }
              phaseRelId = batchPhaseLids[catalogPhase.id];
            } else if (defaultPhaseId) {
              phaseRelId = defaultPhaseId;
            }
          }
        }

        // The first phase can be a catalog phase with no envelope in this
        // payload (see buildUpdatedPayload). Name only an envelope that is in
        // it or added here; with no pointer, apply gives the service its
        // catalog phase, if it has one.
        const envelopes = [...currentPayload, ...toAdd];
        toAdd.push({
          type: "project-services",
          lid: crypto.randomUUID(),
          attributes: {
            name: (rawSvc?.attributes as any)?.name,
            position: startPosition + idx,
            "service-type": serviceType,
          },
          relationships: {
            service: { data: { id: Number(rawSvc.id), type: "services" } },
            ...(phaseRelId != null &&
            hasEnvelope(envelopes, "project-phases", phaseRelId)
              ? {
                  "project-phase": pointerTo(
                    envelopes,
                    "project-phases",
                    phaseRelId
                  ),
                }
              : {}),
          },
        });
      });

      const serviceCount = selectedRawServices.length;
      setSaving(true);
      try {
        await patchPayload([...currentPayload, ...toAdd]);
        setInitialized(false);
        setSelectedRawServices([]);
        setSlideoutOpen(false);
        setSearchInput("");
        setSearchQuery("");
        setSuccessMessage(
          `${serviceCount} service${serviceCount !== 1 ? "s" : ""} added.`
        );
        setShowSuccessAlert(true);
      } catch (err: any) {
        setErrorMessage(
          err?.data?.errors?.[0]?.detail || "Failed to add services."
        );
        setShowFailAlert(true);
      } finally {
        setSaving(false);
      }
    };

    // ── Delete checked services (and their subservices) ──
    const handleDelete = async () => {
      const checkedIds = new Set(
        localServices.filter((s) => s.checked).map((s) => s.id)
      );
      const newPayload = currentPayload.map((item) => {
        if (
          item.type === "project-services" &&
          checkedIds.has(String(item.id ?? item.lid))
        ) {
          return { ...item, attributes: { ...item.attributes, active: false } };
        }
        if (item.type === "project-subservices") {
          const parentId = pointerToken(item.relationships?.["project-service"]);
          if (parentId && checkedIds.has(parentId)) {
            return {
              ...item,
              attributes: { ...item.attributes, active: false },
            };
          }
        }
        return item;
      });

      setSaving(true);
      try {
        await patchPayload(newPayload);
        setInitialized(false);
        const count = checkedIds.size;
        setShowDeleteModal(false);
        setSuccessMessage(`${count} service${count !== 1 ? "s" : ""} removed.`);
        setShowSuccessAlert(true);
      } catch (err: any) {
        setErrorMessage(
          err?.data?.errors?.[0]?.detail || "Failed to remove services."
        );
        setShowFailAlert(true);
      } finally {
        setSaving(false);
      }
    };

    const closeSlideout = () => {
      setSlideoutOpen(false);
      setSelectedRawServices([]);
      setSearchInput("");
      setSearchQuery("");
      setCatalogPage(1);
    };

    // ── Expose payload updates to Edit.tsx (called during the top-level save) ──
    useImperativeHandle(ref, () => ({
      getPayloadUpdates: (basePayload: any[]): any[] =>
        buildUpdatedPayload(
          basePayload,
          serviceType,
          localServices,
          // The phases the editor offers (see BlueprintServiceEditor below).
          localPhases?.length ? localPhases : allPhases
        ),
    }));

    return (
      <div className="blueprintServices blueprintItemsTab">
        {handleAlert(
          showSuccessAlert,
          successMessage,
          setShowSuccessAlert,
          "success",
          ToastAlert
        )}
        {handleAlert(
          showFailAlert,
          errorMessage,
          setShowFailAlert,
          "warning",
          ToastAlert
        )}

        <div
          className={`tabItemsHeader${
            selectedCount || isEmpty ? " hasSelection" : ""
          }`}
        >
          {!readOnly && selectedCount > 0 ? (
            <div className="selectedTabItems">
              <span className="selectedMessage">{selectedCount} Selected</span>
              <FontAwesomeIcon
                icon={faTrashCan}
                className="trashIcon"
                onClick={() => setShowDeleteModal(true)}
              />
            </div>
          ) : isEmpty ? (
            <div className="tabEmpty">{emptyMessage}</div>
          ) : null}
          {!readOnly && (
            <div className="actionBtns">
              <Button className="button" onClick={() => setSlideoutOpen(true)}>
                Add Services
              </Button>
            </div>
          )}
        </div>

        {saving ? (
          <SmallSpinner />
        ) : !isEmpty ? (
          <>
            {configHideEmptyPhases && serviceType !== "managed_services" && (
              <p style={{ color: "#418172" }}>
                <strong>Note:</strong> Only phases with assigned services shown.
                Adding services assigned to phases will cause them to be
                displayed.
              </p>
            )}
            <BlueprintServiceEditor
              serviceType={serviceType}
              readOnly={readOnly}
              phases={(() => {
                const all = localPhases?.length
                  ? localPhases
                  : allPhases.sort((a, b) => a.position - b.position);
                return configHideEmptyPhases &&
                  serviceType !== "managed_services"
                  ? all.filter((phase) =>
                      localServices.some((s) => s.phaseId == phase.id)
                    )
                  : all;
              })()}
              services={localServices}
              setServices={setLocalServices}
              projectResources={projectResources}
              unapprovedIds={unapprovedIds}
            />
          </>
        ) : null}

        <ScopeStackModal
          modalTitle="Remove Services"
          modalBody={
            <p>
              Remove {selectedCount} service{selectedCount !== 1 ? "s" : ""}{" "}
              from this blueprint?
            </p>
          }
          button1Text="Cancel"
          button2Text="Remove"
          handleButton1Click={() => setShowDeleteModal(false)}
          handleButton2Click={handleDelete}
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          deleteModal
        />

        <SlideOutMenuV2
          title={`Add ${snakeToText(serviceType)}`}
          isOpen={slideoutOpen}
          onClose={closeSlideout}
          onSave={handleAdd}
          actionBtnText={`Add${
            selectedAddIds.length ? ` ${selectedAddIds.length}` : ""
          } Selected`}
          actionBtnDisabled={!selectedAddIds.length || saving}
        >
          {saving ? (
            <SmallSpinner />
          ) : (
            <>
              <FormFieldLabel
                label={`Search ${snakeToText(serviceType)}`}
                className={undefined}
              />
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  setSearchQuery(searchInput);
                  setCatalogPage(1);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setSearchQuery(searchInput);
                    setCatalogPage(1);
                  }
                }}
              >
                <SearchField
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onClick={() => {
                    setSearchInput("");
                    setSearchQuery("");
                    setCatalogPage(1);
                  }}
                  fullWidth
                />
              </Form>
              {catalogLoading ? (
                <SmallSpinner />
              ) : (
                <DataTable
                  data={
                    catalogItems.length
                      ? catalogItems
                      : [
                          {
                            id: "empty",
                            name: "No services found.",
                            serviceCategory: "",
                            hours: "",
                          },
                        ]
                  }
                  columns={catalogColumns}
                  selectable={!!catalogItems.length}
                  striped
                  hover
                  selectedIds={selectedAddIds}
                  setSelectedIds={handleCatalogSelection}
                  paginationEnabled
                  pageSizeEnabled
                  currentPage={catalogPage}
                  setCurrentPage={setCatalogPage}
                  pageSize={catalogPageSize}
                  onPageSizeChange={setCatalogPageSize}
                  totalPages={catalogData?.meta?.["page-count"] ?? 1}
                  totalRows={catalogData?.meta?.["record-count"] ?? 0}
                />
              )}
            </>
          )}
        </SlideOutMenuV2>
      </div>
    );
  }
);

ServicesTab.displayName = "ServicesTab";
export default ServicesTab;
